import { Box, Grid, Paper, Button, TextField, CircularProgress } from "@mui/material";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import ButtonTheme from "../../components/ThemeButton/ThemeButton";
import ApiService from "../../functions/apiService";
import { Controller, useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import PageLoader from "../../components/PageLoader/PageLoader";
import RecipientSelect from "../../components/MultiSelect/RecipientSelect";
import CustomGroupForm from "../../components/CustomGroupForm/CustomGroupForm";
import { Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import SendIcon from "@mui/icons-material/Send";
import AddIcon from "@mui/icons-material/Add";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

const BRAND = "#BF1D2D";

const StepHeader = ({ number, title, subtitle }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", mb: "10px" }}>
    <Box
      sx={{
        minWidth: "22px", width: "22px", height: "22px", mt: "2px",
        borderRadius: "50%", backgroundColor: BRAND, color: "#fff",
        fontSize: "12px", fontWeight: 600,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {number}
    </Box>
    <Box>
      <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#101828", lineHeight: 1.3 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "#667085" }}>{subtitle}</Typography>
    </Box>
  </Box>
);

const chipSx = (selected) => ({
  textTransform: "none",
  borderRadius: "8px",
  padding: "5px 14px",
  fontSize: "13px",
  minWidth: "auto",
  lineHeight: 1.6,
  border: "1px solid",
  borderColor: selected ? BRAND : "#D0D5DD",
  color: selected ? BRAND : "#344054",
  backgroundColor: selected ? "rgba(191, 29, 45, 0.06)" : "#fff",
  fontWeight: selected ? 600 : 500,
  "&:hover": {
    borderColor: BRAND,
    backgroundColor: selected ? "rgba(191, 29, 45, 0.10)" : "rgba(191, 29, 45, 0.04)",
  },
});

export default function Messaging() {
  const [customGroupModalOpen, setCustomGroupModalOpen] = useState(false);
  // const { enqueueSnackbar } = useSnackbar();
  let navigate = useNavigate();
  const {
    register, formState: { errors }, control, handleSubmit, setError, setValue, getValues, reset,
    formState: { isDirty, isValid }
  } = useForm({
    defaultValues: {
      sendTo: [],
    },
  });
  const [headers, setHeaders] = useState();
  const [sendTo, setSendTo] = useState();
  const [receiver, setReceiver] = useState();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isSendToRequired, setIsSendToRequired] = useState();
  const [activeTab, setActiveTab] = useState('sendMessage')
  // 'existing' = send to a saved custom group, 'create' = build a new one
  const [customMode, setCustomMode] = useState('existing');
  const [customGroups, setCustomGroups] = useState([]);
  const [customGroupsLoading, setCustomGroupsLoading] = useState(false);
  // const [msg, setMsg] = useState("");

  // The saved custom groups come from /custom-group, not from getMessageTo:
  // getMessageTo returns a single empty entry under its "Custom <group>" key,
  // so it can't be used to pick a group. Sending works either way because the
  // backend resolves any sendTo value starting with "CGROUP" to its members.
  const loadCustomGroups = () => {
    setCustomGroupsLoading(true);
    return ApiService.get("/custom-group")
      .then(({ data }) => {
        const list = data?.data ?? data ?? [];
        setCustomGroups(
          (Array.isArray(list) ? list : []).map((g) => ({
            value: g.SO_VALUE ?? g.value ?? g.id,
            desc: g.SO_DESC ?? g.name ?? g.desc ?? "Untitled group",
          }))
        );
        setCustomGroupsLoading(false);
      })
      .catch(() => {
        setCustomGroupsLoading(false);
        toast.error("Failed to load custom groups");
      });
  };

  // withLoader=false is used to refresh the recipient lists in the background
  // after a custom group is created, so the new group shows up in the picker.
  const loadMessageTo = (withLoader = true) => {
    if (withLoader) setLoading(true);
    return ApiService.get(`/getMessageTo?sort_by_lastname=${true}`).then(({ data }) => {
      setSendTo(data.data);
      const keys = Object.keys(data.data);
      const eachMemberIdx = keys.indexOf("Each Member");
      if (eachMemberIdx > -1) {
        keys.splice(eachMemberIdx, 1);
        keys.unshift("Each Member");
      }
      setHeaders(keys);
      if (withLoader) setLoading(false);
    });
  };

  useEffect(() => {
    loadMessageTo();
  }, []);

  const checkBoxCheckedEvent = (value) => {
    if (value === "Each Member") {
      setIsSendToRequired(false);
      setCustomGroupModalOpen(false);
    } else if (value.includes("Custom")) {
      setCustomGroupModalOpen(true);
    } else {
      setIsSendToRequired(true);
      setCustomGroupModalOpen(false);
    }
  };

  const handleReceiverChange = (value) => {
    checkBoxCheckedEvent(value);
    setReceiver(value);
    setValue("header_type", value, { shouldValidate: true, shouldDirty: true });
    // The recipient picker is rebuilt for the new group, so drop any recipients
    // picked under the previous one instead of silently keeping them.
    setValue("sendTo", []);
    setCustomMode("existing");
    if (value.includes("Custom")) {
      loadCustomGroups();
    }
  };

  const switchCustomMode = (mode) => {
    setCustomMode(mode);
    setValue("sendTo", []);
  };

  const onSubmit = (d, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSending(true)
    let data = {
      header_type: d.header_type,
      message: d.message,
      sendBy: d.sendBy,
    };
    let values = [];
    d.sendTo.map((item) => {
      values.push(item.value);
    });
    data = { ...data, sendTo: values };
    ApiService.post("/sendMessage", data)
      .then((data) => {
        setSending(false)
        toast.success(data?.message)
      })
      .catch(({ data }) => {
        setSending(false)
        toast.error(data?.message)
        setError("message", { type: "manual", message: data.message });
      });
  };

  return (
    <>
      <Paper className="p-3" style={{ marginTop: "1rem" }}>
        <Row>
          <Col md={9} sm={10} xs={10} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: '200px' }} className={'tab-active'}>
              <SendIcon style={{ fontSize: '17px' }} />
              Send A Message
            </div>
            <div style={{ width: '190px' }} className={'tab-white mx-2'}
              onClick={() => navigate("/firehouse/user-messages")}>
              <ForumOutlinedIcon style={{ fontSize: '17px' }} />
              User Messages
            </div>
          </Col>
        </Row>

        <Box className="mt-4 mb-3" sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box
            sx={{
              width: "38px", height: "38px", borderRadius: "10px",
              backgroundColor: "rgba(191, 29, 45, 0.08)", color: BRAND,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <SendIcon style={{ fontSize: "20px" }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>Send a Message</Typography>
            <Typography sx={{ fontSize: "12px", color: "#667085" }}>
              Choose recipients, write your message and pick how it's delivered
            </Typography>
          </Box>
        </Box>

        {loading == true ?
          <Box className="w-100 d-flex justify-content-center align-items-center"
            style={{ height: '460px' }}>
            <PageLoader />
          </Box>
          :
          <form
            onSubmit={handleSubmit((data, event) => {
              onSubmit(data, event);
            })}
          >
            {/* keeps header_type inside the form state — value is driven by the chips below */}
            <input type="hidden" {...register("header_type")} />

            <Grid item columns={{ xs: 12, sm: 12, md: 12 }}>
              <StepHeader number={1} title="Send to" subtitle="Pick a recipient group" />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                {headers &&
                  headers.map((item) => (
                    <Button
                      key={item}
                      type="button"
                      disableRipple
                      onClick={() => handleReceiverChange(item)}
                      sx={{
                        ...chipSx(receiver === item),
                        textTransform: "capitalize",
                      }}
                    >
                      {item === "Each Member" ? "Entire Department" : item.toLowerCase()}
                    </Button>
                  ))}
              </Box>
            </Grid>

            <Grid
              container
              spacing={2}
              columns={{ xs: 12, sm: 12, md: 12 }}
              sx={{ padding: "1rem 0 0 0" }}
            >
              <Grid
                className="pt-2"
                item
                xs={12}
                sm={10}
                md={8}
                sx={{ minHeight: "60px" }}
              >
                <StepHeader
                  number={2}
                  title={customGroupModalOpen == true ? "Custom group" : "Recipients"}
                  subtitle={
                    customGroupModalOpen == true
                      ? customMode === "create"
                        ? "Name the group and pick its members"
                        : "Pick a saved group, or create a new one"
                      : "Select who should receive this"
                  }
                />

                {customGroupModalOpen == true && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", mb: "14px" }}>
                    <Button
                      type="button"
                      disableRipple
                      onClick={() => switchCustomMode("existing")}
                      sx={chipSx(customMode === "existing")}
                    >
                      Select existing group
                    </Button>
                    <Button
                      type="button"
                      disableRipple
                      startIcon={<AddIcon style={{ fontSize: "16px" }} />}
                      onClick={() => switchCustomMode("create")}
                      sx={chipSx(customMode === "create")}
                    >
                      Create new group
                    </Button>
                  </Box>
                )}

                {customGroupModalOpen == true && customMode === "create" ? (
                  <CustomGroupForm
                    array={
                      sendTo ? sendTo["Employee"] : []
                    }
                    onCreated={() => {
                      // Pull the refreshed lists, then hand the user straight to
                      // the picker so they can send to what they just created.
                      loadCustomGroups();
                      loadMessageTo(false).catch(() => { });
                      switchCustomMode("existing");
                    }}
                  />
                ) : customGroupModalOpen == true && customGroupsLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", py: "10px" }}>
                    <CircularProgress size={18} sx={{ color: BRAND }} />
                    <Typography sx={{ fontSize: "13px", color: "#667085" }}>
                      Loading saved groups…
                    </Typography>
                  </Box>
                ) : customGroupModalOpen == true && customGroups.length === 0 ? (
                  <Typography sx={{ fontSize: "13px", color: "#667085", py: "10px" }}>
                    No custom groups yet — use “Create new group” to add one.
                  </Typography>
                ) : (
                  <>
                    {customGroupModalOpen}
                    <RecipientSelect
                      key={customGroupModalOpen == true ? "custom-group" : receiver}
                      size="small"
                      // A message goes to one saved group at a time
                      multiple={customGroupModalOpen != true}
                      placeholder={
                        customGroupModalOpen == true
                          ? "Search and select a saved group"
                          : "Search and select recipients"
                      }
                      array={
                        customGroupModalOpen == true
                          ? customGroups
                          : sendTo && receiver
                            ? sendTo[receiver]
                            : []
                      }

                      register={register}
                      required={
                        getValues()[
                          "header_type"
                        ] == "Each Member"
                          ? false
                          : true
                      }
                    />
                  </>
                )}
                {errors.sendTo && (
                  <p style={{ color: "red" }}>
                    {errors.sendTo?.message}
                  </p>
                )}
              </Grid>

              <Grid
                item
                xs={12}
                sm={10}
                md={8}
                sx={{
                  minHeight: "160px",
                }}
              >
                <StepHeader number={3} title="Message" subtitle="Minimum 1 character required" />
                <TextField
                  className="w-100"
                  multiline
                  rows={4}
                  {...register("message", {
                    required: "message is required",
                    minLength: 1,
                    pattern: /[\w\d]+/,
                  })}
                  // onChange={(e) => setMsg(e.target.value)}
                  placeholder="Enter your message"
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  }}
                />
                {errors.message && (
                  <p style={{ color: "red" }}>
                    {errors.message?.message}
                  </p>
                )}
              </Grid>

              <Grid item xs={12} sm={12} md={12}>
                <StepHeader number={4} title="Send via" subtitle="Choose how it's delivered" />
                <Controller
                  control={control}
                  name="sendBy"
                  size='small'
                  rules={{
                    required:
                      "At leaast one send by should be selected",
                  }}
                  render={({ field }) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                      {[
                        { value: "Push", label: "Push", icon: <NotificationsActiveOutlinedIcon style={{ fontSize: "16px" }} /> },
                        { value: "SMS", label: "Text & Push", icon: <SmsOutlinedIcon style={{ fontSize: "16px" }} /> },
                        { value: "Email", label: "Email", icon: <MailOutlineIcon style={{ fontSize: "16px" }} /> },
                      ].map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          disableRipple
                          startIcon={option.icon}
                          onClick={() => field.onChange(option.value)}
                          onBlur={field.onBlur}
                          sx={chipSx(field.value === option.value)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </Box>
                  )}
                />
                {errors.sendBy && (
                  <p style={{ color: "red" }}>
                    {errors.sendBy?.message}
                  </p>
                )}
              </Grid>
            </Grid>


            <div className="d-flex justify-content-start my-3">
              <ButtonTheme
                type="submit"
                color="primary"
                // isSendToRequired &&
                disabled={isValid == true && !sending ? false : true}
                style={{
                  width: "160px",
                  textTransform: "capitalize",
                  borderRadius: "8px",
                }}
              >

                {sending ? 'Loading...' : 'Send Message'}
              </ButtonTheme>
            </div>
          </form>
        }
      </Paper>
    </>
  );
}
